import React, { useMemo, useCallback } from 'react';
import { Component } from '@/types';
import { useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import * as LucideIcons from 'lucide-react';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SegmentedThemeConfig {
  selectedColor: string;
  unselectedColor: string;
  textColor: string;
  selectedTextColor: string;
  containerBg: string;
  containerBorder: string;
  containerRadius: string;
  segmentRadius: string;
  indicatorStyle: 'fill' | 'underline' | 'pill';
  glowOnSelected: boolean;
}

interface SegmentedControlProps {
  component: Component;
}

interface Segment {
  id: string;
  label: string;
  icon?: string;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, SegmentedThemeConfig> = {
  ios: {
    selectedColor: CONTROL_THEME.primary,
    unselectedColor: CONTROL_THEME.muted,
    textColor: CONTROL_THEME.foreground,
    selectedTextColor: CONTROL_THEME.primaryForeground,
    containerBg: CONTROL_THEME.muted,
    containerBorder: `1px solid ${CONTROL_THEME.border}`,
    containerRadius: '8px',
    segmentRadius: '6px',
    indicatorStyle: 'fill',
    glowOnSelected: false,
  },
  material: {
    selectedColor: '#6200EE',
    unselectedColor: CONTROL_THEME.muted,
    textColor: CONTROL_THEME.mutedForeground,
    selectedTextColor: '#FFFFFF',
    containerBg: CONTROL_THEME.muted,
    containerBorder: `1px solid ${CONTROL_THEME.border}`,
    containerRadius: '4px',
    segmentRadius: '4px',
    indicatorStyle: 'fill',
    glowOnSelected: false,
  },
  pill: {
    selectedColor: '#10B981',
    unselectedColor: 'transparent',
    textColor: CONTROL_THEME.mutedForeground,
    selectedTextColor: '#FFFFFF',
    containerBg: CONTROL_THEME.muted,
    containerBorder: `1px solid ${CONTROL_THEME.border}`,
    containerRadius: '999px',
    segmentRadius: '999px',
    indicatorStyle: 'pill',
    glowOnSelected: false,
  },
  neon: {
    selectedColor: '#00FF88',
    unselectedColor: '#1A1A2E',
    textColor: '#888888',
    selectedTextColor: '#000000',
    containerBg: '#1A1A2E',
    containerBorder: '1px solid #00FF8830',
    containerRadius: '8px',
    segmentRadius: '6px',
    indicatorStyle: 'fill',
    glowOnSelected: true,
  },
  minimal: {
    selectedColor: 'transparent',
    unselectedColor: 'transparent',
    textColor: CONTROL_THEME.mutedForeground,
    selectedTextColor: CONTROL_THEME.foreground,
    containerBg: 'transparent',
    containerBorder: 'none',
    containerRadius: '0',
    segmentRadius: '0',
    indicatorStyle: 'underline',
    glowOnSelected: false,
  },
};

// ─── Size Presets ────────────────────────────────────────────────────────────

const SIZE_CONFIG: Record<string, { height: number; fontSize: number; padding: string; iconSize: number }> = {
  sm: { height: 28, fontSize: 11, padding: '0 8px', iconSize: 12 },
  md: { height: 36, fontSize: 13, padding: '0 12px', iconSize: 16 },
  lg: { height: 44, fontSize: 15, padding: '0 16px', iconSize: 20 },
};

const DEFAULT_SEGMENTS: Segment[] = [
  { id: 'opt1', label: 'Option 1' },
  { id: 'opt2', label: 'Option 2' },
  { id: 'opt3', label: 'Option 3' },
];

// ─── Icon Helper ─────────────────────────────────────────────────────────────

function resolveLucideIcon(name: string): React.FC<{ size?: number; color?: string }> | null {
  const pascalName = name.charAt(0).toUpperCase() + name.slice(1).replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  const Icon = (LucideIcons as Record<string, unknown>)[pascalName] as React.FC<{ size?: number; color?: string }> | undefined;
  return Icon || null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const SegmentedControl: React.FC<SegmentedControlProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;

  // ─── Property Extraction ─────────────────────────────────────────────
  const themeName = (properties.theme as string) ?? 'ios';
  const size = (properties.size as string) ?? 'md';
  const disabled = (properties.disabled as boolean) ?? false;
  const orientation = (properties.orientation as string) ?? 'horizontal';
  const equalWidth = (properties.equalWidth as boolean) ?? true;
  const segments = (properties.segments as Segment[]) ?? DEFAULT_SEGMENTS;
  const selectedIndex = (properties.selectedIndex as number) ?? 0;
  const isHorizontal = orientation === 'horizontal';

  // ─── Theme Resolution ────────────────────────────────────────────────
  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['ios'];
    return {
      ...base,
      ...(properties.selectedColor != null && { selectedColor: properties.selectedColor as string }),
      ...(properties.unselectedColor != null && { unselectedColor: properties.unselectedColor as string, containerBg: properties.unselectedColor as string }),
      ...(properties.textColor != null && { textColor: properties.textColor as string }),
      ...(properties.selectedTextColor != null && { selectedTextColor: properties.selectedTextColor as string }),
    };
  }, [themeName, properties.selectedColor, properties.unselectedColor, properties.textColor, properties.selectedTextColor]);

  const sizeConfig = SIZE_CONFIG[size] ?? SIZE_CONFIG['md'];

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceId as string || component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  const handleSelect = useCallback((index: number) => {
    if (!isViewerMode || disabled) return;
    handlers.onSensorControl?.({
      sensorId,
      action: 'control',
      parameters: { command: 'set', value: index, property: 'selectedIndex' },
    });
  }, [isViewerMode, disabled, handlers, sensorId]);

  // ─── Styles ──────────────────────────────────────────────────────────
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isHorizontal ? 'row' : 'column',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    minHeight: 0,
    padding: 4,
    boxSizing: 'border-box',
    userSelect: 'none',
  };

  const trackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isHorizontal ? 'row' : 'column',
    position: 'relative',
    backgroundColor: theme.containerBg,
    border: theme.containerBorder,
    borderRadius: theme.containerRadius,
    padding: 2,
    gap: 2,
    overflow: 'hidden',
    ...(isHorizontal
      ? { width: '100%', maxWidth: '100%', minWidth: 0 }
      : { height: '100%', maxHeight: '100%' }
    ),
    opacity: disabled ? 0.5 : 1,
  };

  const safeIndex = Math.max(0, Math.min(selectedIndex, segments.length - 1));
  const segCount = segments.length;
  const useSlidingIndicator = theme.indicatorStyle !== 'underline' && equalWidth && segCount > 0;

  // Sliding indicator style (only for fill/pill with equal width)
  const indicatorStyle: React.CSSProperties | undefined = useSlidingIndicator ? {
    position: 'absolute',
    ...(isHorizontal
      ? {
          left: 0,
          top: 2,
          bottom: 2,
          width: `calc(${100 / segCount}% - ${2}px)`,
          transform: `translateX(calc(${safeIndex * 100}% + ${safeIndex}px))`,
        }
      : {
          left: 2,
          right: 2,
          top: 0,
          height: `calc(${100 / segCount}% - ${2}px)`,
          transform: `translateY(calc(${safeIndex * 100}% + ${safeIndex}px))`,
        }),
    borderRadius: theme.segmentRadius,
    backgroundColor: theme.selectedColor,
    transition: 'transform 200ms ease',
    pointerEvents: 'none',
    zIndex: 0,
    ...(theme.glowOnSelected ? { boxShadow: `0 0 10px ${theme.selectedColor}60` } : {}),
  } : undefined;

  return (
    <div
      data-testid={`segmented-control-${component.id}`}
      role="group"
      aria-label="Segmented Control"
      style={containerStyle}
    >
      <div role="tablist" aria-orientation={isHorizontal ? 'horizontal' : 'vertical'} style={trackStyle}>
        {/* Sliding selection indicator */}
        {useSlidingIndicator && <div style={indicatorStyle} />}

        {segments.map((segment, index) => {
          const isSelected = index === safeIndex;
          const IconComp = segment.icon ? resolveLucideIcon(segment.icon) : null;

          const segStyle: React.CSSProperties = {
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            height: isHorizontal ? sizeConfig.height : undefined,
            padding: sizeConfig.padding,
            borderRadius: theme.segmentRadius,
            backgroundColor: useSlidingIndicator ? 'transparent' : (isSelected ? theme.selectedColor : 'transparent'),
            color: isSelected ? theme.selectedTextColor : theme.textColor,
            fontSize: sizeConfig.fontSize,
            fontWeight: isSelected ? 600 : 400,
            cursor: isViewerMode && !disabled ? 'pointer' : 'default',
            border: 'none',
            outline: 'none',
            transition: 'color 200ms ease, font-weight 200ms ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...(equalWidth ? { flex: 1 } : { flex: '0 1 auto' }),
            ...(theme.indicatorStyle === 'underline' && isSelected ? {
              borderBottom: `2px solid ${theme.selectedTextColor}`,
              borderRadius: 0,
            } : {}),
            ...(!useSlidingIndicator && theme.glowOnSelected && isSelected ? {
              boxShadow: `0 0 10px ${theme.selectedColor}60`,
            } : {}),
          };

          return (
            <button
              key={segment.id}
              role="tab"
              aria-selected={isSelected}
              aria-label={segment.label}
              data-testid={`segment-${index}`}
              tabIndex={isViewerMode && !disabled ? 0 : -1}
              onClick={() => handleSelect(index)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && isViewerMode && !disabled) {
                  e.preventDefault();
                  handleSelect(index);
                }
              }}
              style={segStyle}
            >
              {IconComp && <IconComp size={sizeConfig.iconSize} color={isSelected ? theme.selectedTextColor : theme.textColor} />}
              {segment.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
